import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// ============================================
// TYPES
// ============================================

export interface HelpPreferences {
  completedTours: string[];
  dismissedTips: string[];
  completedSetupSteps: string[];
  setupDismissed: boolean;
  firstAccessAt: string;
}

interface UpdateHelpPreferencesDto {
  completedTours?: string[];
  dismissedTips?: string[];
  completedSetupSteps?: string[];
  setupDismissed?: boolean;
}

// ============================================
// CONSTANTES
// ============================================

const STORAGE_KEY = 'help-preferences-fallback';
const QUERY_KEY = ['help-preferences'];

/** Preferências padrão quando não há dados no backend nem no localStorage */
const DEFAULT_PREFERENCES: HelpPreferences = {
  completedTours: [],
  dismissedTips: [],
  completedSetupSteps: [],
  setupDismissed: false,
  firstAccessAt: new Date().toISOString(),
};

// ============================================
// HELPERS DE LOCALSTORAGE (fallback)
// ============================================

function getLocalPreferences(): HelpPreferences | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as HelpPreferences;
  } catch {
    return null;
  }
}

function saveLocalPreferences(prefs: HelpPreferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage indisponível — ignora silenciosamente
  }
}

// ============================================
// HOOK PRINCIPAL
// ============================================

export function useHelpPreferences() {
  const queryClient = useQueryClient();

  // Query: GET /help/preferences
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<HelpPreferences> => {
      try {
        const { data } = await api.get<HelpPreferences>('/help/preferences');
        // Sincroniza localStorage com dados do servidor
        saveLocalPreferences(data);
        return data;
      } catch {
        // Fallback para localStorage em caso de erro de rede
        const local = getLocalPreferences();
        return local ?? { ...DEFAULT_PREFERENCES };
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: 1,
  });

  // Mutation: PATCH /help/preferences
  const mutation = useMutation({
    mutationFn: async (dto: UpdateHelpPreferencesDto): Promise<HelpPreferences> => {
      try {
        const { data } = await api.patch<HelpPreferences>('/help/preferences', dto);
        saveLocalPreferences(data);
        return data;
      } catch {
        // Fallback: salva localmente se a rede falhar
        const current = query.data ?? getLocalPreferences() ?? { ...DEFAULT_PREFERENCES };
        const merged: HelpPreferences = {
          ...current,
          ...dto,
          completedTours: dto.completedTours ?? current.completedTours,
          dismissedTips: dto.dismissedTips ?? current.dismissedTips,
          completedSetupSteps: dto.completedSetupSteps ?? current.completedSetupSteps,
          setupDismissed: dto.setupDismissed ?? current.setupDismissed,
        };
        saveLocalPreferences(merged);
        return merged;
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
    },
  });

  // Dados resolvidos (query ou fallback)
  const preferences: HelpPreferences = query.data ?? getLocalPreferences() ?? { ...DEFAULT_PREFERENCES };

  // ============================================
  // FUNÇÕES AUXILIARES
  // ============================================

  /** Verifica se o tour já foi completado */
  function hasCompletedTour(tourId: string): boolean {
    return preferences.completedTours.includes(tourId);
  }

  /** Verifica se a dica já foi dispensada */
  function hasDismissedTip(tipId: string): boolean {
    return preferences.dismissedTips.includes(tipId);
  }

  /** Marca um tour como completado e persiste */
  function markTourCompleted(tourId: string): void {
    if (hasCompletedTour(tourId)) return;
    const updated = [...preferences.completedTours, tourId];
    mutation.mutate({ completedTours: updated });
  }

  /** Marca uma dica como dispensada e persiste */
  function markTipDismissed(tipId: string): void {
    if (hasDismissedTip(tipId)) return;
    const updated = [...preferences.dismissedTips, tipId];
    mutation.mutate({ dismissedTips: updated });
  }

  return {
    // Dados
    preferences,
    isLoading: query.isLoading,
    isError: query.isError,

    // Mutation
    updatePreferences: mutation.mutate,
    isUpdating: mutation.isPending,

    // Funções auxiliares
    hasCompletedTour,
    hasDismissedTip,
    markTourCompleted,
    markTipDismissed,
  };
}
